import { useCallback, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import { FileCheck } from "lucide-react";
import type { Confirmation } from "../types/confirmations.types";
import { ConfirmationCard } from "./ConfirmationCard";
import { ConfirmationCardPreview } from "./ConfirmationCardPreview";
import { ConfirmationColumn } from "./ConfirmationColumn";
import { CancelConfirmationModal } from "./CancelConfirmationModal";
import { ConfirmTransactionModal } from "./ConfirmTransactionModal";
import { MOCK_PENDING, MOCK_CONFIRMED } from "../data/mockConfirmations";
import useTranslations from "@/shared/lang/useTranslations";

const DROPPABLE_CONFIRMED = "column-confirmed";

type ConfirmationsBoardProps = {
  pending?: Confirmation[];
  confirmed?: Confirmation[];
  onMoveToConfirmed?: (confirmation: Confirmation) => void;
  onCancel?: (id: string, reason?: string) => void;
  onConfirm?: (id: string, attachments?: File[], confirmation_source?: string) => void;
  onViewHistory?: (id: string) => void;
  onView?: (id: string, imageUrl?: string | string[] | null) => void;
  onOpen?: (id: string, imageUrl?: string | string[] | null) => void;
  onNotifyClient?: (id: string) => void;
  onTakerReminder?: (id: string) => void | Promise<void>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

const DEFAULT_PENDING = MOCK_PENDING;
const DEFAULT_CONFIRMED = MOCK_CONFIRMED;

export const ConfirmationsBoard = ({
  pending: controlledPending,
  confirmed: controlledConfirmed,
  onMoveToConfirmed,
  onCancel,
  onConfirm,
  onViewHistory,
  onView,
  onOpen,
  onNotifyClient,
  onTakerReminder,
  onRefresh,
  isRefreshing = false,
}: ConfirmationsBoardProps) => {
  const { t } = useTranslations("confirmations");
  const [internalPending, setInternalPending] = useState<Confirmation[]>(() =>
    controlledPending ? [] : DEFAULT_PENDING
  );
  const [internalConfirmed, setInternalConfirmed] = useState<Confirmation[]>(
    () => (controlledConfirmed ? [] : DEFAULT_CONFIRMED)
  );

  const pending = controlledPending ?? internalPending;
  const confirmed = controlledConfirmed ?? internalConfirmed;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? String(event.over.id) : null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over?.id === DROPPABLE_CONFIRMED) {
        const id = String(active.id);
        const item = pending.find((c) => c.id === id);
        if (item) {
          setActiveId(null);
          setOverId(null);
          setConfirmTargetId(id);
          return;
        }
      }

      setActiveId(null);
      setOverId(null);
    },
    [pending]
  );

  const handleCancel = useCallback(
    (id: string, reason?: string) => {
      if (controlledPending === undefined) {
        setInternalPending((prev) => prev.filter((c) => c.id !== id));
      }
      onCancel?.(id, reason);
    },
    [controlledPending, onCancel]
  );

  const handleCancelClick = useCallback((id: string) => {
    setCancelTargetId(id);
  }, []);

  const handleCancelModalConfirm = useCallback(
    async (reason: string) => {
      if (!cancelTargetId) return;
      setIsCancelling(true);
      try {
        await Promise.resolve(handleCancel(cancelTargetId, reason));
        setCancelTargetId(null);
      } finally {
        setIsCancelling(false);
      }
    },
    [cancelTargetId, handleCancel]
  );

  const handleCancelModalClose = useCallback(() => {
    if (!isCancelling) setCancelTargetId(null);
  }, [isCancelling]);

  const handleConfirm = useCallback(
    (id: string, attachments?: File[], confirmation_source?: string) => {
      const item = pending.find((c) => c.id === id);
      if (!item) return;
      if (controlledPending === undefined) {
        setInternalPending((prev) => prev.filter((c) => c.id !== id));
        setInternalConfirmed((prev) => [
          ...prev,
          { ...item, status: "confirmed" as const, verifiedByAdmin: true },
        ]);
      }
      onMoveToConfirmed?.({ ...item, status: "confirmed" as const, verifiedByAdmin: true });
      return onConfirm?.(id, attachments, confirmation_source);
    },
    [pending, controlledPending, onConfirm, onMoveToConfirmed]
  );

  const handleConfirmClick = useCallback((id: string) => {
    setConfirmTargetId(id);
  }, []);

  const handleConfirmModalConfirm = useCallback(
    async (attachments?: File[], confirmation_source?: string) => {
      if (!confirmTargetId) return;
      setIsConfirming(true);
      try {
        await handleConfirm(confirmTargetId, attachments, confirmation_source);
        setConfirmTargetId(null);
        setActiveId(null);
      } finally {
        setIsConfirming(false);
      }
    },
    [confirmTargetId, handleConfirm]
  );

  const handleConfirmModalClose = useCallback(() => {
    if (!isConfirming) {
      setConfirmTargetId(null);
      setActiveId(null);
    }
  }, [isConfirming]);

  const isDropTarget = overId === DROPPABLE_CONFIRMED;
  const activeConfirmation = activeId
    ? pending.find((c) => c.id === activeId)
    : null;

  const isEmpty = pending.length === 0 && confirmed.length === 0;

  if (isEmpty) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-900/30 px-6 py-12 text-center">
        <FileCheck className="mb-4 h-14 w-14 shrink-0 text-slate-300 dark:text-slate-600" aria-hidden />
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
          {t("board_empty_title")}
        </h2>
        <p className="mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">
          {t("board_empty_description")}
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
        <ConfirmationColumn
          columnId="pending"
          title={t("column_pending")}
          count={pending.length}
          variant="pending"
          emptyMessage={t("column_empty_pending")}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          {pending.map((c) => (
            <ConfirmationCard
              key={c.id}
              confirmation={c}
              hidden={confirmTargetId === c.id}
              onCancel={handleCancelClick}
              onConfirm={handleConfirmClick}
              onViewHistory={onViewHistory}
              onView={onView}
              onOpen={onOpen}
              onNotifyClient={onNotifyClient}
              onTakerReminder={onTakerReminder}
            />
          ))}
        </ConfirmationColumn>

        <ConfirmationColumn
          columnId="confirmed"
          title={t("column_confirmed")}
          count={confirmed.length}
          variant="confirmed"
          droppableId={DROPPABLE_CONFIRMED}
          isDropTarget={isDropTarget}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          {confirmed.map((c) => (
            <ConfirmationCard
              key={c.id}
              confirmation={c}
              onViewHistory={onViewHistory}
              onView={onView}
              onOpen={onOpen}
              onNotifyClient={onNotifyClient}
              onTakerReminder={onTakerReminder}
            />
          ))}
        </ConfirmationColumn>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeConfirmation ? (
          <ConfirmationCardPreview
            confirmation={activeConfirmation}
            isOverlay
          />
        ) : null}
      </DragOverlay>

      <CancelConfirmationModal
        isOpen={cancelTargetId !== null}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelModalConfirm}
        isLoading={isCancelling}
      />

      <ConfirmTransactionModal
        isOpen={confirmTargetId !== null}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        isLoading={isConfirming}
      />
    </DndContext>
  );
};
